import Select from '@/ui/Select';
import { ClearFilterButton } from '@/ui/ClearFilterButton';
import { SortDirection } from '@/enums/sort';
import { TableColumn } from '@/ui/Table';
import clsx from 'clsx';
import { ProductGroupEditorProduct } from '@/components/products-table/use-products-group-editor';
import { ProductsTableData } from '@/components/products-table/use-products-table-view-model';
import {
    createGroupTableHeaderSecondaryRow,
    createGroupTableMetricColumns,
    createGroupTableManageColumn,
    createGroupTableNameColumn,
    renderGroupTableCountDetailsCell,
    renderGroupTableTextFilterInput,
} from '@/components/group-tables/group-table-column-primitives';
import type { ProductPhotoEntity } from '@/entities/product.entity';

type BrandOption = {
    name: string;
    value: string;
};

type CreateProductsTableColumnsArgs = {
    pickedProducts: string[];
    isHeaderChecked: boolean;
    onHeaderCheckboxChange: (checked: boolean) => void;
    nameFilter: string;
    setNameFilter: (value: string) => void;
    productFilter: string;
    setProductFilter: (value: string) => void;
    brandFilter: string | undefined;
    setBrandFilter: (value: string | undefined) => void;
    brandSearch: string | undefined;
    setBrandSearch: (value: string | undefined) => void;
    preparedBrands: BrandOption[];
    onProductAiOpen: (product: ProductsTableData) => void;
    onGroupEditorOpen: (product: ProductsTableData) => void;
    onProductPhotoPreviewOpen: (photos: ProductPhotoEntity[], index: number) => void;
};

export const createProductsTableColumns = ({
    pickedProducts,
    isHeaderChecked,
    onHeaderCheckboxChange,
    nameFilter,
    setNameFilter,
    productFilter,
    setProductFilter,
    brandFilter,
    setBrandFilter,
    brandSearch,
    setBrandSearch,
    preparedBrands,
    onProductAiOpen,
    onGroupEditorOpen,
    onProductPhotoPreviewOpen,
}: CreateProductsTableColumnsArgs) => {
    const renderLeadPhotoPreviewButton = (photos: ProductPhotoEntity[]) => {
        const leadPhoto = photos[0];

        if (!leadPhoto) {
            return null;
        }

        return (
            <button
                type="button"
                aria-label="Открыть фото группы товаров"
                className="mt-0.5 h-[30px] w-[30px] shrink-0 overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onProductPhotoPreviewOpen(photos, 0);
                }}
            >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={leadPhoto.miniSize} alt="Фото товара" className="h-full w-full object-cover" />
            </button>
        );
    };

    const renderPhotoThumbnails = (photos: ProductPhotoEntity[], keyPrefix: string) => {
        if (!photos.length) {
            return null;
        }

        return (
            <div className="flex items-center gap-1 overflow-x-auto">
                {photos.map((photo, photoIndex) => (
                    <button
                        type="button"
                        key={`${keyPrefix}-${photoIndex}`}
                        aria-label={`Открыть фото ${photoIndex + 1}`}
                        className="h-[30px] w-[30px] overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onProductPhotoPreviewOpen(photos, photoIndex);
                        }}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={photo.miniSize}
                            alt={`Фото ${photoIndex + 1}`}
                            className="h-full w-full object-cover"
                        />
                    </button>
                ))}
            </div>
        );
    };

    const groupEditorColumns: TableColumn<ProductGroupEditorProduct>[] = [
        {
            key: 'sku',
            alias: 'Артикул',
            className: 'min-w-[140px] lg:whitespace-nowrap',
            render: (value) => (typeof value === 'string' && value ? value : '-'),
        },
        {
            key: 'platform',
            alias: 'Платформа',
            className: 'min-w-[108px] lg:whitespace-nowrap',
            render: (value) => (typeof value === 'string' && value ? value : '-'),
        },
        {
            key: 'name',
            alias: 'Название',
            className: 'min-w-[260px] max-w-[60vw] lg:max-w-none',
        },
        {
            key: 'brandName',
            alias: 'Бренд',
            className: 'min-w-[140px] lg:whitespace-nowrap',
            render: (value) => (typeof value === 'string' && value ? value : '-'),
        },
        {
            key: 'photos',
            alias: 'Фото',
            className: 'min-w-[120px] max-w-[44vw] lg:max-w-[220px]',
            render: (value, record) => {
                const productPhotos = (value as ProductPhotoEntity[] | null) || [];
                return renderPhotoThumbnails(productPhotos, `${record.id}-group-editor-photo`) || '-';
            },
        },
    ];

    const columns: TableColumn<ProductsTableData>[] = [
        createGroupTableNameColumn<ProductsTableData>({
            render: (value, record) => {
                const product = record as ProductsTableData;
                const productPhotos = product.photos || [];
                const leadPhotoButton = renderLeadPhotoPreviewButton(productPhotos);
                const groupName = typeof value === 'string' && value ? value : '-';

                if (!leadPhotoButton) {
                    return groupName;
                }

                return (
                    <div className="flex min-w-0 items-start gap-2">
                        {leadPhotoButton}
                        <div className="min-w-0 flex-1">{groupName}</div>
                    </div>
                );
            },
        }),
        {
            key: 'brandName',
            alias: 'Бренд',
            sort: SortDirection.NONE,
            className: 'min-w-[96px] md:min-w-[124px]',
            render: (_, record) => record.brandName || '-',
        },
        {
            key: 'productCount',
            alias: 'Продуктов',
            sort: SortDirection.NONE,
            className: 'min-w-[128px] md:min-w-[156px] max-w-[56vw] md:max-w-[34vw] lg:max-w-[260px]',
            render: (_, record) => {
                const product = record as ProductsTableData;

                return (
                    <div className="min-w-0">
                        {renderGroupTableCountDetailsCell({
                            count: product.productCount,
                            details: product.productArticles || [],
                        })}
                    </div>
                );
            },
        },
        ...createGroupTableMetricColumns<ProductsTableData>(),
        {
            key: 'ai',
            alias: 'AI',
            sort: SortDirection.NONE,
            className: 'w-[1%] min-w-[40px] lg:whitespace-nowrap',
            render: (_, record) => {
                const product = record as ProductsTableData;

                if (product.aiAnalysisCount <= 0) {
                    return;
                }

                return (
                    <button
                        type="button"
                        aria-label="Открыть AI-анализы группы продуктов"
                        className="flex h-7 min-w-7 items-center justify-center rounded border border-blue-300 bg-blue-50 px-1 text-[11px] font-semibold leading-none tracking-wide text-blue-700 transition-colors hover:bg-blue-100"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onProductAiOpen(product);
                        }}
                    >
                        AI
                    </button>
                );
            },
        },
        createGroupTableManageColumn<ProductsTableData>({
            onOpen: onGroupEditorOpen,
        }),
    ];

    const headerSecondaryRow = createGroupTableHeaderSecondaryRow({
        columns,
        hasSelectionColumn: pickedProducts !== undefined,
        isHeaderChecked,
        onHeaderCheckboxChange,
        renderFilterCell: (column) => {
            if (column.key === 'name') {
                return (
                    <div className="flex h-[34px] min-w-0 items-center gap-1">
                        <div className="min-w-0 flex-1">
                            {renderGroupTableTextFilterInput({
                                id: 'products-table-filter-name',
                                ariaLabel: 'Фильтр названия группы продуктов',
                                placeholder: 'Группа',
                                value: nameFilter,
                                onChange: setNameFilter,
                            })}
                        </div>
                        {!!nameFilter && (
                            <ClearFilterButton
                                ariaLabel="Очистить фильтр группы продуктов"
                                onClick={() => setNameFilter('')}
                            />
                        )}
                    </div>
                );
            }

            if (column.key === 'brandName') {
                return (
                    <div className="flex h-[34px] min-w-0 items-center gap-1">
                        <Select
                            id="products-table-filter-brand"
                            ariaLabel="Фильтр бренда в таблице групп продуктов"
                            options={preparedBrands}
                            placeholder="Бренд"
                            className={clsx(
                                'h-[34px] min-w-0 flex-1 [&>button]:h-[34px] [&>button]:min-h-[34px] [&>button>input]:text-slate-900',
                                brandFilter
                                    ? '[&>button>input]:placeholder:text-slate-900'
                                    : '[&>button>input]:placeholder:text-slate-400',
                            )}
                            value={brandFilter}
                            onChange={setBrandFilter}
                            withInput
                            searchString={brandSearch}
                            setSearchString={setBrandSearch}
                            uiSize="compact"
                        />
                        {(brandFilter || brandSearch) && (
                            <ClearFilterButton
                                ariaLabel="Очистить фильтр бренда"
                                onClick={() => {
                                    setBrandFilter(undefined);
                                    setBrandSearch(undefined);
                                }}
                            />
                        )}
                    </div>
                );
            }

            if (column.key === 'productCount') {
                return (
                    <div className="flex h-[34px] min-w-0 items-center gap-1">
                        <div className="min-w-0 flex-1">
                            {renderGroupTableTextFilterInput({
                                id: 'products-table-filter-product',
                                ariaLabel: 'Фильтр товара в таблице групп продуктов',
                                placeholder: 'Продукт',
                                value: productFilter,
                                onChange: setProductFilter,
                            })}
                        </div>
                        {!!productFilter && (
                            <ClearFilterButton
                                ariaLabel="Очистить фильтр товара"
                                onClick={() => setProductFilter('')}
                            />
                        )}
                    </div>
                );
            }

            return null;
        },
    });

    return {
        columns,
        headerSecondaryRow,
        groupEditorColumns,
    };
};
